import React, { Dispatch, useReducer } from 'react';

import { localStorage, StorageKey } from '@bimdata/client';

import handleError from '@/actions/common/handle-error.action';
import { getProjects } from '@/admin/api/common';
import { getLogo as getProjectLogo } from '@/api/project';
import { getAvatar } from '@/api/user';

import { Action, State } from './types';
import { AdminedProject } from '@/admin/types/admined-project.dto';

export const ROW_HEIGHT = 52;

export default (): [State, Dispatch<Action>] => {
  const [state, dispatch] = useReducer(
    (state: State, { type, payload }: Action): State => {
      let { list, listSize, pageSize, logos, ownerAvatars, params, needStats } = state;
      switch (type) {
        case 'loadWithOffset': {
          if (listSize !== null && list.length >= listSize) {
            return state;
          }

          getProjects({
            ...(params.search ? { search: params.search } : {}),
            ...(params.archivedFilter !== null ? { archivedFilter: params.archivedFilter } : {}),
            total: listSize === null,
            stats: needStats,
            offset: payload,
            limit: pageSize,
            sortBy: params.sorting[0],
            sortOrder: params.sorting[1] === 'A' ? 'ASC' : 'DESC',
          }).then(
            ({ page, total, stats }) => {
              if (typeof total === 'number') {
                dispatch({ type: 'setListSize', payload: total });
              }
              if (!!stats) {
                dispatch({ type: 'setStats', payload: stats });
              }
              if (page.length) {
                dispatch({
                  type: 'add',
                  payload: {
                    page,
                    params,
                    offset: payload
                  }
                });
              }
            }
          ).catch(
            handleError
          );

          return state;
        };
        case 'add': {
          if ( params.archivedFilter !== payload.params.archivedFilter
            || params.search !== payload.params.search
            || params.sorting[0] !== payload.params.sorting[0]
            || params.sorting[1] !== payload.params.sorting[1]
          ) {
            return state;
          }
          return {
            ...state,
            list: [
              ...(list || []).slice(0, payload.offset),
              ...payload.page,
            ],
          };
        }
        case 'ensure': {
          const project = payload;
          if ((typeof logos[project.id] === 'undefined')) {
            if (project.data.logo) {
              getProjectLogo(project.code, project.data.logo as number)
                .then((data) => dispatch({
                  type: 'setProjectLogo',
                  payload: {
                    pid: project.id,
                    data: data || false
                  }
                }))
                .catch((error) => { handleError(error); });
            }
            logos = {
              ...logos,
              [project.id]: false
            };
          }
          if ((typeof ownerAvatars[project.owner.id] === 'undefined')) {
            if (project.owner.avatar) {
              getAvatar(project.owner.id, project.owner.avatar)
                .then((data) => dispatch({
                  type: 'setProjectOwnerAvatar',
                  payload: {
                    uid: project.owner.id,
                    data: data || false
                  }
                }))
                .catch((error) => { handleError(error); });
            }
            ownerAvatars = {
              ...ownerAvatars,
              [project.owner.id]: false
            };
          }
          return {
            ...state,
            list,
            logos,
            ownerAvatars,
          };
        }
        case 'setProjectLogo': return {
          ...state,
          logos: {
            ...logos,
            [payload.pid]: payload.data,
          }
        };
        case 'setProjectOwnerAvatar': return {
          ...state,
          ownerAvatars: {
            ...ownerAvatars,
            [payload.uid]: payload.data,
          }
        };
        case 'setArchivedFilter': {
          if (params.archivedFilter === payload) {
            return state;
          }
          const newParams = {
            ...params,
            ...(params.archivedFilter && params.sorting[0] === 'archived'
              ? { sorting: [ 'updated', params.sorting[1] ] as [keyof AdminedProject, 'A' | 'D'] }
              : {}
            ),
            archivedFilter: payload
          };
          localStorage.set(StorageKey.ADMIN_PROJECTS_LIST_PARAMS, newParams);
          return {
            ...state,
            list: [],
            listSize: null,
            params: newParams
          };
        };
        case 'setSearch': {
          if (params.search === payload) {
            return state;
          }
          const newParams = {
            ...params,
            search: payload
          };
          localStorage.set(StorageKey.ADMIN_PROJECTS_LIST_PARAMS, newParams);
          return {
            ...state,
            list: [],
            needStats: true,
            listSize: null,
            params: newParams
          };
        };
        case 'setSorting': {
          if (params.sorting[0] === payload[0] && params.sorting[1] === payload[1]) {
            return state;
          }
          const newParams = {
            ...params,
            sorting: payload
          };
          localStorage.set(StorageKey.ADMIN_PROJECTS_LIST_PARAMS, newParams);
          return {
            ...state,
            list: [],
            listSize: null,
            params: newParams
          };
        };
        case 'setPageSize': {
          return {
            ...state,
            pageSize: Math.ceil(payload)
          };
        };
        case 'setListSize': {
          return {
            ...state,
            ...((payload < list.length) ? {
              list: [],
            } : {}),
            listSize: payload
          };
        };
        case 'setStats': {
          return {
            ...state,
            stats: payload,
            needStats: false,
          };
        };
        case 'remove': {
          const l = state.list.filter((p) => !payload.has(p.id));
          const count = state.list.length - l.length;
          if (!count) {
            return state;
          }
          return {
            ...state,
            list: l,
            stats: {
              total: state.stats.total && (state.stats.total - count),
              active: state.stats.active,
              archived: state.stats.archived && (state.stats.archived - count),
            },
            needStats: false,
          }
        }
        case 'resetItems': {
          const m = new Map<number, AdminedProject>(payload.map((p) => [p.id, p]));
          let modified = false;
          const newLogos = { ...logos };
          const l = state.list.map((p) => {
            if (m.has(p.id)) {
              modified = true;
              const np = m.get(p.id)!;
              if (p.data.logo !== np.data.logo) {
                if (np.data.logo) {
                  dispatch({ type: 'ensure', payload: np });
                } else {
                  delete newLogos[p.id];
                }
              }
              return np;
            }
            return p;
          });
          if (!modified) {
            return state;
          }
          return {
            ...state,
            list: l,
            logos: newLogos
          }
        }
      }
      return state;
    },
    {
      list: [],
      listSize: null,
      pageSize: Math.ceil(1.5 * window.innerHeight/ROW_HEIGHT),
      logos: {},
      ownerAvatars: {},
      params: localStorage?.get(StorageKey.ADMIN_PROJECTS_LIST_PARAMS) || {
        archivedFilter: null,
        sorting: ['created', 'D'],
        search: '',
        viewMode: 'table'
      },
      stats: {
        total: null,
        active: null,
        archived: null
      },
      needStats: true,
    }
  );
  return [state, dispatch];
};
